// Who you are, in the platform masthead.
//
// ── WHY THIS IS ITS OWN FILE AND LOADS ITSELF LATE ───────────────────────
// `TickerChrome` renders inside the PRERENDERED homepage, and that document is
// built under Node. `@/integrations/supabase/client` touches `localStorage` at
// import time and throws there, which fails the build for the whole front door
// — `tickerHomeWiring.test.ts` guards the prerendered tree against exactly that
// import for exactly that reason.
//
// So the session is read through a DYNAMIC import inside an effect. An effect
// never runs during the prerender, so Node never reaches the module, and the
// served HTML contains no account line at all — it appears after hydration.
// That also means the badge can never differ between the server's HTML and the
// browser's first render, because both render nothing.
//
// ── WHY IT EXISTS ────────────────────────────────────────────────────────
// On 2026-09-12 a week-old barn session in one browser sent every tool click to
// a barn dashboard, and nothing anywhere said whose session it was. It cost an
// hour. My Barn now says (#2067); this is the same answer on the platform side.
//
// The owner has three user populations — free, paid terminal, and barn — and
// says some people will hold several. "Which account am I in?" is a standing
// question, not an edge case.

import { useEffect, useState } from "react";

export default function SignedInBadge() {
  const [email, setEmail] = useState<string | null>(null);

  useEffect(() => {
    let live = true;

    // Dynamic, so the prerender never loads the Supabase client. See above.
    void import("@/integrations/supabase/client")
      .then(async ({ supabase }) => {
        const { data } = await supabase.auth.getSession();
        if (live) setEmail(data.session?.user?.email ?? null);

        // Sign-in and sign-out both have to move this without a reload —
        // otherwise the masthead keeps claiming an account that is gone, which
        // is a worse lie than saying nothing.
        const { data: sub } = supabase.auth.onAuthStateChange((_e, session) => {
          if (live) setEmail(session?.user?.email ?? null);
        });
        return sub;
      })
      .then((sub) => {
        if (!live) sub?.subscription.unsubscribe();
      })
      .catch(() => {
        /* No session module, no badge. Never break the masthead over this. */
      });

    return () => {
      live = false;
    };
  }, []);

  // ⚠️ THE GOLD BUTTON LIVES HERE TOO (owner's screenshot, 2026-09-14): it read
  // "Sign in" right beside a signed-in email. It now says "Account" once a
  // session is known. The FIRST render is always the signed-out pair — on the
  // server and in the browser alike — so the prerendered HTML and hydration
  // still agree, and a visitor without JavaScript still gets working links.
  return (
    <>
      {email && (
        <span className="rbtx-whoami" title={`Signed in as ${email}`}>
          {email}
        </span>
      )}
      {/* ONE DOOR (owner, 2026-10-25): signed out, the gold button is Join — the
          membership page, with the price and the form — and Sign in is the quiet
          link beside it, to the same page on its sign-in tab. Signed in: Account. */}
      {!email && (
        <a className="rbtx-signin-quiet" href="/join?signin=1">
          Sign in
        </a>
      )}
      <a className="rbtx-signin" href={email ? "/account" : "/membership"}>
        {email ? "Account" : "Join"}
      </a>
    </>
  );
}
